import React, { useState, useEffect, useRef } from 'react';

/**
 * GenericSearchSelect - A searchable dropdown component with a premium feel.
 */
export function GenericSearchSelect({ 
    value, 
    options, 
    onChange, 
    placeholder = "Suchen...", 
    allowCustom = false,
    emptyLabel = "— Keine Auswahl —"
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const containerRef = useRef(null);

    // Sync search term with selected value label
    useEffect(() => {
        let selected;
        const isGrouped = options.length > 0 && options[0].options;
        if (isGrouped) {
            options.forEach(group => {
                const found = group.options.find(o => String(o.value) === String(value));
                if (found) selected = found;
            });
        } else {
            selected = options.find(o => String(o.value) === String(value));
        }
        setSearchTerm(selected ? selected.label : (allowCustom ? value : ''));
    }, [value, options, allowCustom]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const isGrouped = options.length > 0 && options[0].options;
    
    const filtered = isGrouped 
        ? options.map(group => ({
            ...group,
            options: group.options.filter(o => (o.label || '').toLowerCase().includes(searchTerm.toLowerCase()))
          })).filter(group => group.options.length > 0)
        : options.filter(o => (o.label || '').toLowerCase().includes(searchTerm.toLowerCase()));

    const handleSelect = (opt) => {
        onChange(opt.value);
        setIsOpen(false);
    };

    const handleTextChange = (val) => {
        setSearchTerm(val);
        if (allowCustom) {
            onChange(val);
        }
        if (!isOpen) setIsOpen(true);
    };

    return (
        <div className="employee-search-select" ref={containerRef}>
            <div 
                className={`search-select-input-container ${isOpen ? 'open' : ''}`}
                onClick={() => setIsOpen(true)}
            >
                <input 
                    className="employee-edit-field no-border" 
                    value={searchTerm}
                    onChange={e => handleTextChange(e.target.value)}
                    onFocus={() => setIsOpen(true)}
                    placeholder={placeholder}
                />
                <span 
                    className="search-select-arrow" 
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsOpen(!isOpen);
                    }}
                >
                    {isOpen ? '▲' : '▼'}
                </span>
            </div>
            {isOpen && (
                <div className="search-select-dropdown">
                    <div className="search-select-option" onClick={() => handleSelect({ value: '', label: '' })}>
                        <span className="option-name" style={{ color: '#8e8e93', fontStyle: 'italic' }}>{emptyLabel}</span>
                    </div>
                    {filtered.length === 0 && !allowCustom ? (
                        <div style={{ padding: '12px', textAlign: 'center', fontSize: '12px', color: '#8e8e93' }}>
                            Keine Treffer
                        </div>
                    ) : (
                        isGrouped ? (
                            filtered.map(group => (
                                <div key={group.title}>
                                    <div className="search-select-group-title" style={{ padding: '6px 14px', fontSize: '11px', fontWeight: 'bold', color: '#8e8e93', background: '#f5f5f7', textTransform: 'uppercase' }}>
                                        {group.title}
                                    </div>
                                    {group.options.map(opt => (
                                        <div 
                                            key={opt.value} 
                                            className={`search-select-option ${String(opt.value) === String(value) ? 'selected' : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleSelect(opt);
                                            }}
                                        >
                                            <div className="option-info">
                                                <span className="option-name">{opt.label}</span>
                                                {opt.subLabel && <span className="option-role">{opt.subLabel}</span>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ))
                        ) : (
                            filtered.map(opt => (
                                <div 
                                    key={opt.value} 
                                    className={`search-select-option ${String(opt.value) === String(value) ? 'selected' : ''}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleSelect(opt);
                                    }}
                                >
                                    <div className="option-info">
                                        <span className="option-name">{opt.label}</span>
                                        {opt.subLabel && <span className="option-role">{opt.subLabel}</span>}
                                    </div>
                                </div>
                            ))
                        )
                    )}
                </div>
            )}
        </div>
    );
}
